import Dexie, { type EntityTable } from 'dexie'
import { performanceDiagnostics } from './performance-diagnostics'
import type { AppSetting, CaptureProfile, RankQueueItem, SessionJourney, SessionRecord } from '@/domain/types'
import type { BuffDefinition } from '@/buffs/types'
import type { ManboClip } from '@/buffs/manbo'

class MapleLogDatabase extends Dexie {
  profiles!: EntityTable<CaptureProfile, 'id'>
  sessions!: EntityTable<SessionRecord, 'id'>
  sessionJourneys!: EntityTable<SessionJourney, 'sessionId'>
  rankQueue!: EntityTable<RankQueueItem, 'id'>
  settings!: EntityTable<AppSetting, 'key'>
  buffs!: EntityTable<BuffDefinition, 'id'>
  manboClips!: EntityTable<ManboClip, 'key'>

  constructor() {
    super('maple-log')
    this.version(1).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      observations: '++id, sessionId, [sessionId+capturedAt], capturedAt',
      events: 'id, sessionId, [sessionId+capturedAt], type, capturedAt',
      settings: 'key, updatedAt',
    })
    this.version(2).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      observations: null,
      events: null,
      settings: 'key, updatedAt',
    })
    this.version(3).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      settings: 'key, updatedAt',
    }).upgrade((transaction) => transaction.table('sessions').clear())
    this.version(4).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      settings: 'key, updatedAt',
      rankQueue: 'id, nextAttemptAt, createdAt',
    })
    this.version(5).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      settings: 'key, updatedAt',
      rankQueue: null,
    })
    this.version(6).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      sessionJourneys: 'sessionId, updatedAt',
      settings: 'key, updatedAt',
    })
    this.version(7).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      sessionJourneys: 'sessionId, updatedAt',
      rankQueue: 'id, sessionId, status, nextAttemptAt, createdAt',
      settings: 'key, updatedAt',
    })
    this.version(8).stores({ buffs: 'id, order, fingerprint, sourceSkillId, updatedAt' })
    // Retain the old schema step so installed v9 databases can upgrade safely.
    this.version(9).stores({ speechClips: 'key, usedAt', speechRequests: 'key' })
    // Keep the historical schema so installed databases can drop obsolete audio data safely.
    this.version(10).stores({ speechClips: null, speechRequests: null })
    this.version(11).stores({ manboClips: 'key, usedAt' })
  }
}

export const db = new MapleLogDatabase()
const MIN_HISTORY_DURATION_MS = 5_000
const discardedSessionIds = new Set<string>()

export async function saveSessionState(session: SessionRecord, journey: SessionJourney | null, snapshotAt = Date.now()): Promise<void> {
  if (session.status === 'ended' || session.status === 'idle' || session.activeDurationMs <= MIN_HISTORY_DURATION_MS) return
  const snapshot: SessionRecord = {
    ...session, status: 'ended', endedAt: snapshotAt, pausedAt: undefined, persistenceState: 'autosaved',
  }
  const historyJourney = journey ? { ...journey, pendingExpSince: undefined } : null
  const startedAt = performance.now()
  const release = performanceDiagnostics.acquire('pending-saves')
  try {
    await db.transaction('rw', db.sessions, db.sessionJourneys, async () => {
      if (discardedSessionIds.has(session.id)) return
      const stored = await db.sessions.get(session.id)
      // Final history (including legacy records) always wins over delayed checkpoints.
      if (stored?.status === 'ended' && stored.persistenceState !== 'autosaved') return
      if (stored?.persistenceState === 'autosaved' && (stored.endedAt ?? 0) > snapshotAt) return
      await db.sessions.put(snapshot)
      if (historyJourney) await db.sessionJourneys.put(historyJourney)
    })
  } finally {
    release()
    performanceDiagnostics.measure('save', performance.now() - startedAt)
  }
}

export async function deleteSessionState(sessionId: string): Promise<void> {
  await db.transaction('rw', db.sessions, db.sessionJourneys, db.rankQueue, async () => {
    await db.sessions.delete(sessionId)
    await db.sessionJourneys.delete(sessionId)
    await db.rankQueue.where('sessionId').equals(sessionId).delete()
  })
}

export async function clearSessionHistory(): Promise<void> {
  await db.transaction('rw', db.sessions, db.sessionJourneys, db.rankQueue, async () => {
    await db.sessions.clear()
    await db.sessionJourneys.clear()
    await db.rankQueue.clear()
  })
}

export async function saveEndedSessionState(
  session: SessionRecord,
  journey: SessionJourney | null,
  rankItems: readonly RankQueueItem[],
): Promise<void> {
  const startedAt = performance.now()
  const release = performanceDiagnostics.acquire('pending-saves')
  try {
    if (session.activeDurationMs <= MIN_HISTORY_DURATION_MS) discardedSessionIds.add(session.id)
    await db.transaction('rw', db.sessions, db.sessionJourneys, db.rankQueue, async () => {
      if (session.activeDurationMs <= MIN_HISTORY_DURATION_MS) {
        await db.sessions.delete(session.id)
        await db.sessionJourneys.delete(session.id)
        await db.rankQueue.where('sessionId').equals(session.id).delete()
        return
      }
      await db.sessions.put({ ...session, persistenceState: 'finalized' })
      if (journey) await db.sessionJourneys.put({ ...journey, pendingExpSince: undefined })
      if (session.rankIneligibleReason) await db.rankQueue.where('sessionId').equals(session.id).delete()
      else if (rankItems.length) await db.rankQueue.bulkPut([...rankItems])
    })
  } catch (error) {
    discardedSessionIds.delete(session.id)
    throw error
  } finally {
    release()
    performanceDiagnostics.measure('save', performance.now() - startedAt)
  }
}
